import {
  AdvancedReportMetricRow,
  AdvancedReportMetricRowsResult
} from 'features/advanced-reports/advanced-reports.types';
import { MmmCampaignDrilldownModelOutputDto } from '../models/mmm-campaign-drilldown-model-output';
import { calculateRoasBase } from 'features/reports/functions/metric-calculation-functions';
import { MmmModelOutputDto } from 'features/marketing-mix-modelling/budget-scenario-planner/models/mmm-model-output';

export const createMmmCampaignDrilldownModelOutputs = (
  metrics: AdvancedReportMetricRowsResult,
  mmmOutput: MmmModelOutputDto[]
): MmmCampaignDrilldownModelOutputDto[] => {
  const allMetrics = [...metrics.revenues, ...metrics.costs, ...metrics.sales];

  const uniqueMetricIdsSet = new Set<string>([
    ...allMetrics.map((metric) => metric.metricId)
  ]);

  const uniqueMetricIds = [...uniqueMetricIdsSet];
  const metricRows = uniqueMetricIds.map((metricId) => {
    const metric = allMetrics.find((m) => m.metricId === metricId)!;
    const metricName =
      metric.metricParentId === ''
        ? metric.metricTypeName
        : metric.metricParentId;
    const mmmOutputDataForSegment = mmmOutput.find(
      (output) => output.segmentValue === metricName
    );
    const hasChildren = allMetrics.some((m) => m.metricParentId === metricId);

    const cost = getSummedMetricValuesById(metrics.costs, metricId);
    const revenue = getSummedMetricValuesById(metrics.revenues, metricId);
    const sales = getSummedMetricValuesById(metrics.sales, metricId);
    const roas = calculateRoasBase(cost, revenue);

    const parentCost = hasChildren
      ? cost
      : getSummedMetricValuesById(metrics.costs, metric.metricParentId);

    const parentRevenue = hasChildren
      ? revenue
      : getSummedMetricValuesById(metrics.revenues, metric.metricParentId);

    const shareFlattenedCost = Math.ceil((cost / parentCost) * 100);
    const plannedSpend =
      (shareFlattenedCost / 100) * (mmmOutputDataForSegment?.spend ?? 0);

    const parentRoas = hasChildren
      ? roas
      : calculateRoasBase(parentCost, parentRevenue);
    const blendedMMMROAS =
      (roas / parentRoas) * (mmmOutputDataForSegment?.mROAS ?? 0);

    const estimatedRevenue = calculateEstimatedRevenue(
      plannedSpend,
      revenue,
      blendedMMMROAS,
      roas
    );

    let estimatedSales = 0;
    let cpa = 0;

    if (sales > 0 && revenue > 0) {
      estimatedSales = estimatedRevenue / (revenue / sales);
    }

    if (estimatedSales > 0) {
      cpa = plannedSpend / estimatedSales;
    }

    const row: MmmCampaignDrilldownModelOutputDto = {
      id: metricId,
      parentId: metric.metricParentId,
      hasChildren,
      depth: metric.depth,
      field: metric.metricTypeName,
      shouldBlurField: metric.isMetricTypeNameSensitive,
      cost,
      revenue,
      roas,
      mROAS: mmmOutputDataForSegment?.mROAS ?? 0,
      plannedSpend: isNaN(Number(plannedSpend.toFixed(2)))
        ? 0
        : Number(plannedSpend.toFixed(2)),
      blendedMMMROAS: isNaN(blendedMMMROAS) ? 0 : blendedMMMROAS,
      estimatedRevenue: isNaN(estimatedRevenue) ? 0 : estimatedRevenue,
      estimatedSales: isNaN(estimatedSales) ? 0 : estimatedSales,
      cpa: isNaN(cpa) ? 0 : cpa
    };

    return row;
  });

  return metricRows;
};

function getSummedMetricValuesById(
  metrics: AdvancedReportMetricRow[],
  metricId: string
): number {
  const summedMetrics = metrics
    .filter((metric) => metric.metricId === metricId)
    .map((metric) => metric.metricValue)
    .reduce((total, metricValue) => (total += metricValue), 0);

  return summedMetrics;
}

function calculateEstimatedRevenue(
  plannedSpend: number,
  revenue: number,
  blendedMMMROAS: number,
  roas: number
): number {
  const scaleFactor = 1.3;

  if (blendedMMMROAS > 0) {
    return blendedMMMROAS * plannedSpend;
  } else if (roas > 0) {
    return roas * plannedSpend;
  } else if (!plannedSpend || plannedSpend === 0) {
    return revenue;
  } else {
    return scaleFactor * plannedSpend;
  }
}
